import type { CreateInvoiceRequest } from '@/types/billing';

const DB_NAME = 'saaspos-pos-offline';
const STORE = 'pending-sales';
const DB_VERSION = 1;

export interface PendingPosSale {
  clientRequestId: string;
  createdAt: string;
  body: CreateInvoiceRequest;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'clientRequestId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

export async function enqueuePendingSale(body: CreateInvoiceRequest): Promise<string> {
  const clientRequestId = crypto.randomUUID();
  const entry: PendingPosSale = {
    clientRequestId,
    createdAt: new Date().toISOString(),
    body: { ...body, clientRequestId },
  };
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(entry);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
  return clientRequestId;
}

export async function listPendingSales(): Promise<PendingPosSale[]> {
  const db = await openDb();
  const rows = await new Promise<PendingPosSale[]>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result as PendingPosSale[]);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return rows.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function removePendingSale(clientRequestId: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(clientRequestId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export function isLikelyNetworkError(err: unknown): boolean {
  if (!navigator.onLine) return true;
  if (err && typeof err === 'object') {
    const e = err as { code?: string; message?: string; response?: unknown };
    if (e.code === 'ERR_NETWORK' || e.message?.includes('Network Error')) return true;
    if (!e.response && e.message) return true;
  }
  return false;
}
